import React, { useState, useEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { setSnackInfo } from "../redux/actions/appAction";
import {
    Card,
    Chip,
    Typography,
    Box,
    Paper,
    Grid,
    TextField,
    MenuItem,
    CircularProgress,
    TablePagination,
    Tooltip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import HistoryIcon from "@material-ui/icons/History";
import moment from "moment";
import { APPROVAL_TYPES } from "../config/approvalTypesRegistry";



const STAGE_FILTERS = [
    { value: "all", label: "All stages" },
    { value: "pending", label: "In progress" },
    { value: "DGM/AGM", label: "Pending DGM/AGM" },
    { value: "Finance Manager", label: "Pending Finance" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
];

// Node status → icon + colour. Kept in one place so the trail reads
// consistently no matter which approval type rendered it.
const NODE_STYLES = {
    completed: { color: "#2e7d32", Icon: CheckCircleIcon },
    rejected: { color: "#c62828", Icon: CancelIcon },
    current: { color: "#ed6c02", Icon: HourglassEmptyIcon },
    pending: { color: "#ed6c02", Icon: HourglassEmptyIcon },
    not_reached: { color: "#bdbdbd", Icon: RadioButtonUncheckedIcon },
};

const HEADLINE_COLORS = {
    Approved: { bg: "#e8f5e9", color: "#2e7d32" },
    Completed: { bg: "#e8f5e9", color: "#2e7d32" },
    Rejected: { bg: "#ffebee", color: "#c62828" },
    "In Progress": { bg: "#fff4e5", color: "#ed6c02" },
};

const useStyles = makeStyles((theme) => ({
    filterBar: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
        borderRadius: theme.spacing(1),
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
    card: {
        borderRadius: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    cardHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        padding: theme.spacing(2, 3),
        borderBottom: "1px solid #f0f0f0",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    cardContent: {
        padding: theme.spacing(3),
    },
    amountChip: {
        fontWeight: 700,
        color: "#1A3C6E",
    },
    stageChip: {
        fontWeight: 600,
    },
    // The trail lays out horizontally on desktop and stacks on mobile, so a
    // five-node chain stays readable on a phone.
    timeline: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(2),
        borderTop: "1px dashed #e0e0e0",
    },
    node: {
        flex: "1 1 180px",
        minWidth: 160,
    },
    nodeLabel: {
        fontWeight: 600,
        fontSize: "0.8rem",
    },
    nodeMeta: {
        fontSize: "0.72rem",
        color: "#6b7280",
        lineHeight: 1.5,
    },
    emptyState: {
        padding: theme.spacing(8),
        textAlign: "center",
        borderRadius: theme.spacing(2),
    },
    loadingOverlay: {
        display: "flex",
        justifyContent: "center",
        padding: theme.spacing(8),
    },
    paginationContainer: {
        marginTop: theme.spacing(2),
        backgroundColor: "#fff",
        borderRadius: theme.spacing(1),
        boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
    },
}));

const TimelineNode = ({ node, classes }) => {
    const style = NODE_STYLES[node.status] || NODE_STYLES.not_reached;
    const { Icon } = style;
    const dimmed = node.status === "not_reached";

    return (
        <Box className={classes.node}>
            <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                <Icon style={{ fontSize: 18, color: style.color }} />
                <Typography
                    className={classes.nodeLabel}
                    style={{ color: dimmed ? "#9ca3af" : "#111827" }}
                >
                    {node.label}
                </Typography>
            </Box>
            <Typography className={classes.nodeMeta} style={{ color: style.color, fontWeight: 600 }}>
                {node.statusLabel}
            </Typography>
            {node.actor && (
                <Typography className={classes.nodeMeta}>{node.actor}</Typography>
            )}
            {node.date && (
                <Typography className={classes.nodeMeta}>
                    {moment(node.date).format("DD MMM YYYY, hh:mm A")}
                </Typography>
            )}
            {node.remarks && (
                <Tooltip title={node.remarks}>
                    <Typography className={classes.nodeMeta} noWrap style={{ fontStyle: "italic" }}>
                        “{node.remarks}”
                    </Typography>
                </Tooltip>
            )}
        </Box>
    );
};

const ExternalApprovalHistory = ({ typeKey, setSnackInfo: setSnackInfoProp }) => {
    const classes = useStyles();
    const [rows, setRows] = useState([]); // [{ type, row }]
    const [totalDocs, setTotalDocs] = useState(0);
    const [fetching, setFetching] = useState(false);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [stage, setStage] = useState("all");
    const [search, setSearch] = useState("");
    // Debounced copy of `search`, so typing doesn't fire a request per keystroke.
    const [appliedSearch, setAppliedSearch] = useState("");

    const isAll = typeKey === "all";
    const activeTypes = useMemo(
        () =>
            (isAll ? APPROVAL_TYPES : APPROVAL_TYPES.filter((t) => t.key === typeKey))
                .filter((t) => typeof t.fetchHistory === "function"),
        [typeKey, isAll]
    );

    useEffect(() => {
        const timer = setTimeout(() => {
            setAppliedSearch(search);
            setPage(0);
        }, 400);
        return () => clearTimeout(timer);
    }, [search]);

    const fetchHistory = useCallback(async () => {
        if (activeTypes.length === 0) {
            setRows([]);
            setTotalDocs(0);
            return;
        }
        setFetching(true);
        try {
            const params = { page: page + 1, limit: rowsPerPage, stage, search: appliedSearch };
            const perType = await Promise.all(
                activeTypes.map(async (type) => {
                    try {
                        const response = await type.fetchHistory(params);
                        return {
                            rows: (response?.data?.data || []).map((row) => ({ type, row })),
                            totalDocs: response?.data?.totalDocs || 0,
                        };
                    } catch (error) {
                        console.error(`Failed to fetch ${type.key} history:`, error);
                        return { rows: [], totalDocs: 0 };
                    }
                })
            );
            setRows(perType.flatMap((r) => r.rows));
            setTotalDocs(perType.reduce((sum, r) => sum + r.totalDocs, 0));
        } catch (error) {
            setSnackInfoProp(error?.response?.data?.message || "Something went wrong", "error");
            setRows([]);
            setTotalDocs(0);
        }
        setFetching(false);
    }, [activeTypes, page, rowsPerPage, stage, appliedSearch, setSnackInfoProp]);

    useEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    if (activeTypes.length === 0) {
        return null;
    }

    return (
        <div>
            <Paper className={classes.filterBar}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6} md={4}>
                        <TextField
                            label="Search"
                            placeholder="Prospect, referrer, lead no. or raised by"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <TextField
                            select
                            label="Stage"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={stage}
                            onChange={(e) => {
                                setStage(e.target.value);
                                setPage(0);
                            }}
                        >
                            {STAGE_FILTERS.map((f) => (
                                <MenuItem key={f.value} value={f.value}>
                                    {f.label}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
            </Paper>

            {fetching ? (
                <div className={classes.loadingOverlay}>
                    <CircularProgress />
                </div>
            ) : rows.length === 0 ? (
                <Paper className={classes.emptyState}>
                    <HistoryIcon style={{ fontSize: 60, color: "#9ca3af", marginBottom: 16 }} />
                    <Typography variant="h6">No requests found</Typography>
                    <Typography variant="body2" color="textSecondary">
                        Nothing matches the current filters.
                    </Typography>
                </Paper>
            ) : (
                rows.map(({ type, row }) => {
                    const headline = HEADLINE_COLORS[row.currentStageStatus] || HEADLINE_COLORS["In Progress"];
                    return (
                        <Card key={`${type.key}_${type.rowKey(row)}`} className={classes.card}>
                            <div className={classes.cardHeader}>
                                <Box>
                                    <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: "wrap" }}>
                                        {type.icon}
                                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                                            {type.title(row)}
                                        </Typography>
                                        <Chip size="small" label={type.label} variant="outlined" style={{ fontSize: "0.7rem" }} />
                                        {row.experienceCenterName && (
                                            <Chip size="small" label={row.experienceCenterName} variant="outlined" style={{ fontSize: "0.7rem" }} />
                                        )}
                                    </Box>
                                    <Typography variant="caption" color="textSecondary">
                                        Raised {row.createdAt ? moment(row.createdAt).format("DD MMM YYYY") : "-"}
                                        {row.updatedAt ? ` · Last updated ${moment(row.updatedAt).fromNow()}` : ""}
                                    </Typography>
                                </Box>
                                <Box display="flex" alignItems="center" style={{ gap: 16, flexWrap: "wrap" }}>
                                    <Chip
                                        size="small"
                                        className={classes.stageChip}
                                        label={row.currentStage}
                                        style={{ backgroundColor: headline.bg, color: headline.color }}
                                    />
                                    <Typography variant="h6" className={classes.amountChip}>
                                        ₹{Number(type.amount(row) || 0).toLocaleString()}
                                    </Typography>
                                </Box>
                            </div>
                            <div className={classes.cardContent}>
                                {type.renderCardBody(row, classes)}
                                {Array.isArray(row.timeline) && row.timeline.length > 0 && (
                                    <div className={classes.timeline}>
                                        {row.timeline.map((node) => (
                                            <TimelineNode key={node.key} node={node} classes={classes} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Card>
                    );
                })
            )}

            {totalDocs > 0 && (
                <div className={classes.paginationContainer}>
                    <TablePagination
                        component="div"
                        count={totalDocs}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                        labelRowsPerPage="Rows per page:"
                    />
                </div>
            )}
        </div>
    );
};

export default connect(null, { setSnackInfo })(ExternalApprovalHistory);
